<script lang="ts" context="module">
  export function iso(date: Date) {
    const pad = (n: any) => (n < 10 ? "0" + n : n);
    return (
      date.getFullYear() +
      "-" +
      pad(date.getMonth() + 1) +
      "-" +
      pad(date.getDate())
    );
  }
</script>

<script lang="ts">
  import { createEventDispatcher, onMount } from "svelte";
  import { t } from "../translations/i18n";

  import FaArrowLeft from "svelte-icons/fa/FaArrowLeft.svelte";
  import FaArrowRight from "svelte-icons/fa/FaArrowRight.svelte";
  import FaArrowCircleRight from "svelte-icons/fa/FaArrowCircleRight.svelte";
  import FaArrowCircleLeft from 'svelte-icons/fa/FaArrowCircleLeft.svelte'
  import FaRegTimesCircle from "svelte-icons/fa/FaRegTimesCircle.svelte";
  import Button from "./button.svelte";
  import {
    checkDateIncludedInArray
  } from "../data-views/utils";

  export let available_dates: Array<Date> = [];
  /** Default value of the date picker */
  export let value: string | Date = iso(new Date());
  export let aggregationMode: boolean = false;
  /** For external control of apply button */
  export let applyButtonDisabled: boolean = false;
  export let days = "Mo|Tu|We|Th|Fr|Sa|Su".split("|");
  export let months = "Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec".split(
    "|"
  );
  /** First day of the week (0 = Sunday, 1 = Monday) */
  export let start = 1;
  /** Offset in months from currently selected date */
  export let offset = 0;
  /** If true - shows calendar, if false - shows month and year picker */
  export let isDaily = false;

  // Add these type definitions before the weeksFrom function
  type CalendarDay = {
    date: number;      // day of month
    value: string;     // ISO date string
    class: string;     // CSS classes for styling
    disabled?: boolean; // optional disabled state
  }
  type CalendarWeek = CalendarDay[];

  type CalendarGrid = CalendarWeek[];

  const month_list_short = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ]

  const month_rows_short = [
    ["Jan", "Feb", "Mar"],
    ["Apr", "May", "Jun",],
    ["Jul", "Aug", "Sep"],
    ["Oct", "Nov", "Dec"],
  ];

  let availableMonthlyDatesDict: any = {};

  let date: any = iso(new Date());

  let selected_month: any;
  let selected_year: number;
  let disable_apply_button: boolean = false;
  const dispatch = createEventDispatcher();


  $: selected_month = selected_month || month_list_short[new Date(value).getMonth()];
  $: selected_year = selected_year || new Date(value).getFullYear();
  $: disabled_months = availableMonthlyDatesDict[selected_year] ? month_list_short.filter(m => !availableMonthlyDatesDict[selected_year].includes(m)) : month_list_short;

  $: acceptDate(value);

  $: viewDate = viewDateFrom(date, offset);

  $: month = months[viewDate.getMonth()];

  $: year = viewDate.getFullYear();

  $: weeks = generateCalendarGrid(viewDate, date, start);

  /**
   * Validates and converts an input date value (string/number/Date) to ISO format
   * @param value
   */
  function acceptDate(value: string | number | Date) {
    const newDate: Date | number = new Date(value);

    if (!isNaN(newDate.getTime())) {
      date = iso(newDate);
    }
  }

  /**
   * Handles navigation between months/years
   * 
   * Daily: Moves by 1 month
   * 
   * Monthly: Moves by 12 months (1 year)
   * 
   * @param direction
   */
  function go(direction: any) {
    if (isDaily){
      offset = offset + direction;
      selected_year = selected_year + direction;
      disable_apply_button = true;   
    }
    else {
      offset = offset + direction * 12;
      selected_year = selected_year + direction;
      disable_apply_button = true;
      selected_month = null;
    }
  }

  /**
   * Updates both the internal date state and exposed value
   * @param newValue
   */
  function selectDate(newValue: any) {
    date = newValue;
    value = newValue;
    offset = 0;
  }

  /**
   * Creates a new date by adding the month offset to the given date
   * 
   * Used for calendar navigation
   * 
   * @param date - date to view
   * @param offset - offset in months
   * 
   * @returns viewDate - date to view
   */
  function viewDateFrom(date: number, offset: number) {
    var viewDate = new Date(date);
    viewDate.setMonth(viewDate.getMonth() + offset);
    return viewDate;
  }

  /**
   * Generates calendar grid data for the month view
   * 
   * @param viewDate - current view date
   * @param date - selected date
   * @param start - first day of the week (0 = Sunday, 1 = Monday)
   * 
   * @returns weeks - array of weeks with days
   */
  function generateCalendarGrid(viewDate: Date, date: string, start: number) {
    var first = new Date(viewDate.getTime());
    first.setDate(1);
    first.setDate(first.getDate() + ((start - first.getDay() - 7) % 7));

    var last = new Date(viewDate.getTime());
    last.setDate(
      new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0).getDate()
    );
    last.setDate(last.getDate() + ((start - last.getDay() + 6) % 7));

    var d = new Date(first.getTime()),
      M = viewDate.getMonth(),
      Y = viewDate.getFullYear(),
      week: any[] = [],
      weeks = [week];

  
    while (d.getTime() <= last.getTime()) {
      var dd = d.getDate(),
      mm = d.getMonth(),
      yy = d.getFullYear(),
      value = iso(d);

      week.push({
        date: dd,
        value,
        // class for styling
        class: [
          date === value ? "selected" : "",
          mm == M ? "" : (mm > M ? yy >= Y : yy > Y) ? "future" : "past",
          checkDateIncludedInArray(available_dates, d) ? "" : "disabled"
        ].join(" "),
      });

      d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);

      if (d.getDay() === start) {
        week = [];
        weeks.push(week);
      }
    }

    return weeks;
  }

  /**
   * Handles date selection confirmation
   */
  function dateSelectionButtonHandler() {
    if (isDaily) {
      const selectedDate = new Date(date.split("-")[0], date.split("-")[1] - 1, date.split("-")[2]);
      dispatch('dateChanged', selectedDate);
    }
    else {
      const selectedDate = new Date(selected_year, month_list_short.indexOf(selected_month), 1);
      dispatch('dateChanged', selectedDate);
    }
  }

  /**
   * Handles closing the date picker
   */
  function closeDatePicker() {
    dispatch('closeDatePicker');
  }

  /**
   * Handles month selection
   * 
   * @param month - selected month
   */
  function handleMonthClick(month: string) {
    selected_month = month;
    disable_apply_button = false;
  }

  onMount(() => {
    if (!isDaily) {
      // Form a dictionary from available dates where key is year and value is an array of months
      availableMonthlyDatesDict = available_dates.reduce((acc: any, date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        if (!acc[year]) {
          acc[year] = [];
        }
        if (!acc[year].includes(month)) {
          acc[year].push(month_list_short[month]);
        }
        return acc;
      }, {});

      let parsed_value = new Date(value);

      selected_month = month_list_short[parsed_value.getMonth()];
      selected_year = parsed_value.getFullYear();
    }
  });

</script>

<!-- Daily date picker -->
{#if isDaily}
  <table>
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <tr>
      <td class="nav-icon-wrapper" on:click={() => go(-1)}>
        <FaArrowLeft/>
      </td>
      <td colspan="5">{$t(month)} {year}</td>
      <td class="nav-icon-wrapper" on:click={() => go(+1)}>
        <FaArrowRight/>
      </td>
    </tr>
    <tr>
      {#each days as day}
        <th>{$t(day)}</th>
      {/each}
    </tr>
    {#each weeks as week}
      <tr>
        {#each week as day}
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <td class="btn {day.class}" on:click={() => selectDate(day.value)}
            >{day.date}</td
          >
        {/each}
      </tr>
    {/each}
  </table>

<!-- Monthly date picker -->
{:else}
  <!-- Calendar-like monthpicker (months in table (4 months per row), years selectable above months)-->
  <table>
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <tr>
      <td class="nav-icon-wrapper" on:click={() => go(-1)}>
        <FaArrowLeft/>
      </td>
      <td>{year}</td>
      <td class="nav-icon-wrapper" on:click={() => go(+1)}>
        <FaArrowRight/>
      </td>
    </tr>
    {#each month_rows_short as row}
      <tr>
        {#each row as month}
            <!-- svelte-ignore a11y-click-events-have-key-events -->
            <td class="btn monthly {disabled_months.includes(month) ? 'disabled' : ''} {month == selected_month ? (disabled_months.includes(month) ? '' : 'selected') : ''}" on:click={() => handleMonthClick(month)}
            >{$t(month)}</td
          >
        {/each}
      </tr>
    {/each}
  </table>
{/if}
<div class="button-wrapper">
  <Button handler={dateSelectionButtonHandler} iconOnTheLeft={false} title={$t("Apply")} disabled={applyButtonDisabled ? true : (isDaily ? false : disable_apply_button)}>
    <FaArrowCircleRight slot="icon" />
    <svelte:fragment slot="text">{$t("Apply")}</svelte:fragment>
  </Button>
  {#if !aggregationMode}
    <Button handler={closeDatePicker} iconOnTheLeft={false} title={$t("Close")}>
      <FaRegTimesCircle slot="icon" />
      <svelte:fragment slot="text">{$t("Close")}</svelte:fragment>
    </Button>
  {:else}
    <Button handler={closeDatePicker} iconOnTheLeft={false} title={$t("Back")}>
      <FaArrowCircleLeft slot="icon" />
      <svelte:fragment slot="text">{$t("Back")}</svelte:fragment>
    </Button>
  {/if}
</div>

<style lang="scss">
  td,
  th {
    width: 28px;
    text-align: center;
    border-radius: 4px;
    line-height: 24px;
    margin: 0;
    padding: 0;
  }

  tr {
    color: var(--color-black);
  }

  td.past,
  td.future {
    opacity: 0.5;
  }

  td.disabled {
    cursor: not-allowed;
    pointer-events: none;
    background-color: adjust-color(gray, $lightness: -10%);

    &:hover {
      background-color: adjust-color(gray, $lightness: -10%);
      color: var(--color-black);
    }
  }

  td.past.disabled,
  td.future.disabled {
    background-color: adjust-color(gray, $lightness: +5%);
  }

  .btn {
    cursor: pointer;

    &:hover {
      background: rgba(#35b472, 0.65);
      color: var(--color-white);
    }

    &.monthly {
      width: 90px;
      height: 40px;
    }
  }

  .nav-icon-wrapper {
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 20px;
    width: 100%;

    &:hover {
      color: var(--color-gray);
    }
  }

  td.selected {
    color: var(--color-white);
    font-weight: bold;
    background-color: var(--color-primary);
    border-color: var(--color-secondary);
  }

  .monthly-date-picker {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 0 16px;
  }

  .selection-col {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 50%;
    padding: 0 16px 16px 16px;
    margin-top: 0;
  }

  .button-wrapper {
    margin-top: 1rem;
    display: flex;
    flex-direction: row;
    justify-content: center;
    gap: 0.5rem;
  }
</style>
